import { PRERENDER_DATA_SCRIPT_ID } from '@wanderlust/shared';
import type { BlogCategory, BlogPost, BlogPostSummary } from '@wanderlust/shared';

// Re-exported so the pages and their tests keep reading the id from the module
// that owns the parsing, while the value itself stays shared with the API.
export { PRERENDER_DATA_SCRIPT_ID };

/**
 * How many posts the homepage blog band shows. The prerender build reads this
 * so the posts baked into the HTML match what the page refetches on mount —
 * a different count would swap the band out from under the reader.
 */
export const HOME_BLOG_POST_COUNT = 3;

/**
 * Posts per page on the blog index. The prerender build reads this so the page
 * baked into the HTML is the same page the index asks for on mount — a
 * different size would reshuffle the grid under the reader.
 */
export const BLOG_LIST_PAGE_SIZE = 12;

/**
 * The blog index as the build left it: the unfiltered first page, the count the
 * pagination is drawn from, and the categories the filter pills are. Enough to
 * render the whole page without asking the API for any of it.
 */
export interface PrerenderedBlogList {
  posts: BlogPostSummary[];
  total: number;
  categories: BlogCategory[];
}

interface PrerenderData {
  blogPost?: BlogPost;
  homePosts?: BlogPostSummary[];
  blogList?: PrerenderedBlogList;
  /** Published posts in the family-travel category, for the Turkey Trot page. */
  familyPosts?: BlogPostSummary[];
}

function readPrerenderData(): PrerenderData | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const script = document.getElementById(PRERENDER_DATA_SCRIPT_ID);
  if (!script?.textContent) {
    return null;
  }

  try {
    return JSON.parse(script.textContent) as PrerenderData;
  } catch {
    return null;
  }
}

export function readPrerenderedBlogPost(expectedSlug?: string): BlogPost | null {
  const post = readPrerenderData()?.blogPost ?? null;

  if (!post) {
    return null;
  }

  if (expectedSlug && post.slug !== expectedSlug) {
    return null;
  }

  return post;
}

/**
 * The homepage's blog band, as it was at build time. Hydration renders from
 * this so the band in the prerendered HTML survives the client takeover; the
 * page then refetches to pick up anything published since the last deploy.
 */
export function readPrerenderedHomePosts(): BlogPostSummary[] | null {
  const posts = readPrerenderData()?.homePosts;
  return Array.isArray(posts) ? posts : null;
}

/**
 * The blog index's first page, as it was at build time. Hydration renders from
 * this so the grid in the prerendered HTML survives the client takeover; the
 * page then refetches to pick up anything published since the last deploy.
 */
export function readPrerenderedBlogList(): PrerenderedBlogList | null {
  const list = readPrerenderData()?.blogList;
  return Array.isArray(list?.posts) ? list : null;
}

/**
 * The Turkey Trot page's family-travel row, as it was at build time. Hydration
 * renders from this so the row in the prerendered HTML survives the client
 * takeover; the page then refetches to pick up anything published since the
 * last deploy, which is what lets the row appear without a deploy.
 */
export function readPrerenderedFamilyPosts(): BlogPostSummary[] | null {
  const posts = readPrerenderData()?.familyPosts;
  return Array.isArray(posts) ? posts : null;
}
